import React, { useLayoutEffect, useState } from 'react';

/**
 * Перенос оформления на узел, вынесенный порталом в <body>.
 *
 * ЗАЧЕМ ПОРТАЛ (корень измерен живьём в Chromium в атоме WEB-712): корень
 * экрана обоих рантаймов несёт класс `.funnel-screen-in` с анимацией
 * появления, объявленной как
 * `animation: funnel-screen-in var(--screen-fade-ms, 0ms) ease-out both`
 * (frontend/src/styles/public-paywall.css). Из-за `animation-fill-mode: both`
 * анимация продолжает применять значения последнего кадра и ПОСЛЕ окончания,
 * а анимированный `transform` вычисляется в матрицу `matrix(1, 0, 0, 1, 0, 0)`,
 * а не в ключевое слово `none` — хотя в keyframes записано `transform: none`.
 * Любое значение `transform`, кроме `none`, делает элемент содержащим блоком
 * для потомков с `position: fixed`. Поэтому всё, что внутри экрана объявлено
 * фиксированным, позиционируется от экрана, а не от вьюпорта. Лечится выносом
 * узла порталом в <body>, мимо любых предков с transform.
 *
 * С 21.10.2026 заливка у `.funnel-screen-in` — `backwards` (ветка
 * stylepack/cta-dock-anim, ради прибитой панели кнопки `.qb-cta-dock`): после
 * конца анимации матрицы на корне больше нет. Но ПОКА анимация идёт
 * (`--screen-fade-ms` пака > 0), transform на корне действует и опора для
 * `position: fixed` та же — поэтому портал остаётся.
 *
 * ЗАЧЕМ ЭТОТ ХУК: сборка поверхности воронки (funnelSurfaceStyle) кладёт
 * переменные оформления ИНЛАЙНОМ на корень страницы (`.public-paywall-main`
 * либо, во встроенном режиме, `[data-testid=public-paywall-embedded]`), а НЕ
 * на `:root`. Портал выносит узел из-под этого корня, наследование обрывается
 * и оформление сваливается в фолбэки (а `:root` кабинета из global.css даёт
 * вообще чужую палитру). Переменные приходится передать явно — снятыми с
 * якоря, оставленного на месте узла в потоке.
 *
 * Потребители (один список на всех, WEB-1043): попап даунселла (WEB-712) и
 * блок cookie-согласия (WEB-1043).
 */

/**
 * Переменные, без которых вынесенная порталом карточка теряет вид.
 *
 * Намеренно НЕ переносятся `--color-text-primary` / `--color-text-secondary`:
 * их пиннит белыми слой контента экрана с фото-вуалью, потому что текст там
 * лежит ПОВЕРХ затемнённой картинки. У вынесенных карточек своя непрозрачная
 * подложка (`--color-bg-surface`), и белый текст на ней был бы невидим; цвет
 * текста карточки резолвят через `--color-text-on-surface` (WEB-966/WEB-1000),
 * а он переносится. По той же причине карточка не имеет права пиннить себе
 * `--color-text-primary` — за порталом он придёт с `:root` кабинета.
 */
export const PORTAL_SURFACE_VARS = [
  '--color-bg-surface',
  '--color-text-on-surface',
  '--color-primary',
  '--color-on-primary',
  '--color-error',
  '--color-border-primary',
  '--font-family-primary',
  '--border-radius-lg',
  '--border-radius-md',
  '--shadow-medium',
] as const;

/**
 * Снять переменные оформления с ТОГО места, где узел стоял бы в потоке.
 * Якорь — пустой невидимый узел, оставленный на месте блока: он наследует
 * ровно тот же каскад, что унаследовал бы узел без портала.
 */
export function usePortalSurfaceVars(anchor: HTMLElement | null): React.CSSProperties {
  const [vars, setVars] = useState<React.CSSProperties>({});
  // Layout-эффект, а не обычный: переменные обязаны встать ДО первого кадра,
  // иначе посетитель успевает увидеть карточку в фолбэчных цветах.
  useLayoutEffect(() => {
    if (!anchor || typeof window === 'undefined' || !window.getComputedStyle) return;
    const computed = window.getComputedStyle(anchor);
    const next: Record<string, string> = {};
    for (const name of PORTAL_SURFACE_VARS) {
      const value = computed.getPropertyValue(name).trim();
      if (value) next[name] = value;
    }
    setVars(next as React.CSSProperties);
  }, [anchor]);
  return vars;
}
